import React from 'react'

import { ZariContext } from '_zari/context'
import { AdminContext } from 'contexts/AdminContext'
import { Portlet, Table, Button, Col, Row, Form, Modal } from '_zari/core'

import EstadosOrdenCompraConstant from 'constants/estadosOrdenCompra'

import * as OrdenCompraService from 'services/ordenCompra.service'
import * as UsuarioService from 'services/usuario.service'


const portletBreadcrumbs = [{ name: 'Aprobación Ordenes de Compra' }]

const tableHeaderRows = [
  { label: 'SERIE-NRO' },
  { label: 'FECHA' },
  { label: 'PROVEEDOR' },
  { label: 'GLOSA' },
  { label: 'MONTO' },
  { label: 'TIPO APROB.' },
  { label: 'ESTADO' },
  { label: '' }
]

const AprobacionOrdenCompraPage = () => {
  const __zariContext = React.useContext(ZariContext)
  const __adminContext = React.useContext(AdminContext)
  const tablaRef = React.useRef(null)

  const [tipoAprobacion, setTipoAprobacion] = React.useState('')
  const [mostrarAccionModal, setMostrarAccionModal] = React.useState(false)
  const [ordenSeleccionada, setOrdenSeleccionada] = React.useState(null)
  const [accionSeleccionada, setAccionSeleccionada] = React.useState('')
  const [motivo, setMotivo] = React.useState('')
  const [enviando, setEnviando] = React.useState(false)

  const [esGerencia, setEsGerencia] = React.useState(false)
  const [esLogistica, setEsLogistica] = React.useState(false)

  React.useEffect(() => {
    UsuarioService.obtenerFlagsActual()
      .then(({ data }) => {
        setEsGerencia(!!data.flagGerencia)
        setEsLogistica(!!data.flagLogistica)
        // Auto-set filter based on user role y recargar tabla con ese tipo.
        let tipoInicial = ''
        if (data.flagGerencia) tipoInicial = 'GERENCIA'
        else if (data.flagLogistica) tipoInicial = 'LOGISTICA'
        if (tipoInicial) {
          setTipoAprobacion(tipoInicial)
          if (tablaRef.current && tablaRef.current.reload) {
            tablaRef.current.reload({ tipoAprobacion: tipoInicial })
          }
        }
      })
      .catch(() => {})
  }, [])

  const clickBuscar = React.useCallback(() => {
    if (!tipoAprobacion) {
      __zariContext.snack('warning', 'Seleccione un tipo de aprobación')
      return
    }
    tablaRef.current.reload({ tipoAprobacion })
  }, [tipoAprobacion, __zariContext])

  const clickAccion = React.useCallback((orden, accion) => {
    setOrdenSeleccionada(orden)
    setAccionSeleccionada(accion)
    setMotivo('')
    setMostrarAccionModal(true)
  }, [])

  const confirmarAccion = React.useCallback(async () => {
    if ((accionSeleccionada === 'OBSERVAR' || accionSeleccionada === 'RECHAZAR') && !motivo.trim()) {
      __zariContext.snack('warning', 'Debe ingresar un motivo.')
      return
    }
    setEnviando(true)
    try {
      await OrdenCompraService.aprobarDesdeVista(ordenSeleccionada._id, {
        accion: accionSeleccionada,
        motivo
      })
      const msgs = { APROBAR: 'Orden aprobada', OBSERVAR: 'Orden observada', RECHAZAR: 'Orden rechazada' }
      __zariContext.snack('success', msgs[accionSeleccionada] || 'Acción realizada')
      setMostrarAccionModal(false)
      tablaRef.current.reload({ tipoAprobacion })
    } catch (err) {
      const error = err?.data?.error || 'Error al procesar la acción'
      __zariContext.snack('error', error)
    } finally {
      setEnviando(false)
    }
  }, [accionSeleccionada, motivo, ordenSeleccionada, tipoAprobacion, __zariContext])

  const [pdfPreviewUrl, setPdfPreviewUrl] = React.useState('')
  const [mostrarPdfPreview, setMostrarPdfPreview] = React.useState(false)

  const clickPreviewPdf = React.useCallback(
    id => {
      __zariContext.loader.open('Cargando PDF...')
      OrdenCompraService.descargarxId(id)
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          setPdfPreviewUrl(url)
          setMostrarPdfPreview(true)
        })
        .catch(() => __zariContext.snack('error', 'Error al cargar PDF'))
        .finally(() => __zariContext.loader.close())
    },
    [__zariContext]
  )

  const closePdfPreview = React.useCallback(() => {
    setMostrarPdfPreview(false)
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl)
      setPdfPreviewUrl('')
    }
  }, [pdfPreviewUrl])

  return (
    <>
      <Portlet>
        <Portlet.Header title='Aprobación de Ordenes de Compra' breadcrumbs={portletBreadcrumbs} />
        <Portlet.Body>
          <Row className='p-3'>
            <Col md={3}>
              <Form.Group>
                <Form.Label text='TIPO APROBACIÓN' htmlFor='filtro-tipo' md={6} />
                <Col md={6}>
                  <Form.Select
                    id='filtro-tipo'
                    value={tipoAprobacion}
                    onChange={e => setTipoAprobacion(e.target.value)}
                  >
                    <option value=''>Seleccionar</option>
                    {esGerencia && <option value='GERENCIA'>Gerencia</option>}
                    {esLogistica && <option value='LOGISTICA'>Logística</option>}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button onClick={clickBuscar}>Buscar</Button>
            </Col>
          </Row>
          <Table.ServerSide
            ref={tablaRef}
            useNest={true}
            url='/ordenes-compra/pendientes-aprobacion'
            initialParams={{ tipoAprobacion: tipoAprobacion || '__none__' }}
            headRows={tableHeaderRows}
            rowTemplate={row => (
              <Table.Row key={row._id}>
                <Table.Cell>
                  {row.serieDocumento}-{row.numeroDocumento}
                </Table.Cell>
                <Table.Cell>{row.fechaEmision || '--'}</Table.Cell>
                <Table.Cell title={row.rucSocio}>{row.razonSocial || '--'}</Table.Cell>
                <Table.Cell>{row.glosa || '--'}</Table.Cell>
                <Table.Cell>
                  {row.moneda === 'USD' ? 'US$ ' : 'S/ '}
                  {(row.montoSubTotal || 0).toFixed(2)}
                </Table.Cell>
                <Table.Cell>
                  <span className={`badge badge-${row.tipoAprobacionRequerida === 'GERENCIA' ? 'danger' : 'info'}`}>
                    {row.tipoAprobacionRequerida}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {row.estado && EstadosOrdenCompraConstant.__dictionary[row.estado] ? (
                    <span className={`badge badge-${EstadosOrdenCompraConstant.__dictionary[row.estado].color}`}>
                      {EstadosOrdenCompraConstant.__dictionary[row.estado].nombre}
                    </span>
                  ) : (
                    '--'
                  )}
                </Table.Cell>
                <Table.Cell>
                  <div className='d-flex align-items-center'>
                    <Button
                      size='sm'
                      variant='success'
                      className='mr-1'
                      title='Aprobar'
                      onClick={() => clickAccion(row, 'APROBAR')}
                    >
                      <i className='fa fa-check' />
                    </Button>
                    <Button
                      size='sm'
                      variant='warning'
                      className='mr-1'
                      title='Observar'
                      onClick={() => clickAccion(row, 'OBSERVAR')}
                    >
                      <i className='fa fa-eye' />
                    </Button>
                    <Button
                      size='sm'
                      variant='danger'
                      className='mr-1'
                      title='Rechazar'
                      onClick={() => clickAccion(row, 'RECHAZAR')}
                    >
                      <i className='fa fa-times' />
                    </Button>
                    <Button
                      size='sm'
                      variant='outline-secondary'
                      title='Ver PDF'
                      onClick={() => clickPreviewPdf(row._id)}
                    >
                      <i className='fa fa-file-pdf' />
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          />
        </Portlet.Body>
      </Portlet>
      {mostrarAccionModal && (
        <Modal show={mostrarAccionModal} onHide={() => setMostrarAccionModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>
              {accionSeleccionada === 'APROBAR' && 'Aprobar Orden de Compra'}
              {accionSeleccionada === 'OBSERVAR' && 'Observar Orden de Compra'}
              {accionSeleccionada === 'RECHAZAR' && 'Rechazar Orden de Compra'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {ordenSeleccionada && (
              <div className='mb-3'>
                <strong>Orden:</strong> {ordenSeleccionada.serieDocumento}-{ordenSeleccionada.numeroDocumento}
                <br />
                <strong>Proveedor:</strong> {ordenSeleccionada.razonSocial}
                <br />
                <strong>Monto:</strong> {ordenSeleccionada.moneda === 'USD' ? 'US$ ' : 'S/ '}
                {(ordenSeleccionada.montoSubTotal || 0).toFixed(2)}
              </div>
            )}
            {(accionSeleccionada === 'OBSERVAR' || accionSeleccionada === 'RECHAZAR') && (
              <Form.Group>
                <Form.Label text='Motivo' htmlFor='motivo-accion' />
                <textarea
                  id='motivo-accion'
                  className='form-control'
                  rows={3}
                  maxLength={500}
                  value={motivo}
                  onChange={e => setMotivo(e.target.value)}
                  placeholder='Ingrese el motivo...'
                />
              </Form.Group>
            )}
            {accionSeleccionada === 'APROBAR' && <p>¿Está seguro que desea aprobar esta orden de compra?</p>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setMostrarAccionModal(false)} disabled={enviando}>
              Cancelar
            </Button>
            <Button
              variant={
                accionSeleccionada === 'APROBAR' ? 'success' : accionSeleccionada === 'RECHAZAR' ? 'danger' : 'warning'
              }
              onClick={confirmarAccion}
              disabled={enviando}
            >
              {enviando ? 'Procesando...' : 'Confirmar'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {mostrarPdfPreview && (
        <Modal show={mostrarPdfPreview} size='lg'>
          <Modal.Header>
            <Modal.Title>Orden de Compra</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: '70vh' }}>
            <iframe src={pdfPreviewUrl} style={{ width: '100%', height: '100%', border: 'none' }} title='PDF Preview' />
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={closePdfPreview}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

export default React.memo(AprobacionOrdenCompraPage)
